import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import classNames from 'classnames';
import { Skeleton } from 'antd';
import { useSelector } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendar } from '@fortawesome/pro-regular-svg-icons/faCalendar';
import { faLocationDot } from '@fortawesome/pro-regular-svg-icons/faLocationDot';
import { faArrowsRotate } from '@fortawesome/pro-solid-svg-icons/faArrowsRotate';
import { isAccountAdmin, isCollaborator } from 'shared/store/selectors/auth.selector';
import { getLang } from 'shared/store/selectors/lang.selector';
import { toExternalUrl } from 'shared/utils';
import { ROUTES } from 'shared/constants/routes';
import { EVENTS, SCHEDULE_EVENT_TYPES } from 'shared/constants';
import { CADENCE_LABEL_KEYS } from 'shared/utils/recurrence';
import { useAttendanceModeLabels } from 'shared/hooks/schedule.hooks';
import { Button } from 'src/components/design-system';
import { Tooltip } from 'src/components/core/tooltip/tooltip.component';
import { useCheckInSession, useCheckIns } from 'shared/hooks/attendance.hooks';
import { useCheckInBoundaryRefetch } from 'shared/hooks/check-in-boundary.hooks';
import { formatCheckInWindowLabel } from 'shared/utils/check-in-window';
import analyticsService from 'src/helpers/analytics.service';
import monitorService from 'src/helpers/monitor.service';
import { useEducationalEventsContext } from '../../educational-events-context';
import { useEducationalEventFormat } from '../../helpers/use-educational-event-format';
import { ShowQrModal } from '../check-in/show-qr-modal';
import { CheckInClosedModal } from '../check-in/check-in-closed-modal';
import { SelfAttestButton } from '../check-in/self-attest-button';
import { EventResources } from '../event-resources/event-resources';
import {
  resolveHosts,
  isCurrentUserHost,
  isHostOrAdmin,
  isCheckInEligibleToday,
  isCheckInSessionOpen,
  getCheckInSchedule,
  isPastEvent,
  isStaticQr,
} from '../../helpers/event-view.helpers';
import { CheckInToggleRow } from './check-in-toggle-row';
import { CheckedInCard } from './checked-in-card';
import { DeleteEventButton } from './delete-event-button';
import styles from './event-detail-panel.module.scss';

function InfoField({ label, value, sub }) {
  return (
    <div className={styles.infoField}>
      <span className={styles.infoLabel}>{label}</span>
      <span className={styles.infoValue}>{value || '—'}</span>
      {sub && (
        <span className={styles.infoSub}>
          {sub.label} {sub.value}
        </span>
      )}
    </div>
  );
}

export function EventDetailPanel() {
  const {
    lang,
    schedulesLang,
    users,
    userId,
    selectedEvent,
    loadingDetail,
    errorDetail,
    refreshSelectedEvent,
    openEditForm,
  } = useEducationalEventsContext();
  const isAdmin = useSelector(isAccountAdmin);
  const createLang = useSelector(getLang('CREATE_EDUCATIONAL_EVENT'));
  const { formatDayWithWeekday, formatDateWithTime, formatTime, formatOrgTime } =
    useEducationalEventFormat();
  const { getFormatLabel } = useAttendanceModeLabels();

  const [qrOpen, setQrOpen] = useState(false);

  const isHost = isCurrentUserHost({ event: selectedEvent, userId });
  const isManager = isHostOrAdmin({ event: selectedEvent, userId, isAdmin });
  const canManageCheckIn = useSelector(isCollaborator);

  const {
    session,
    isOpen,
    loaded: sessionLoaded,
    error: sessionError,
    pending,
    closedSummary,
    clearClosedSummary,
    open,
    close,
  } = useCheckInSession(selectedEvent, {
    enabled: canManageCheckIn,
    onError: (error, context) =>
      monitorService.error('Check-in session action failed', {
        error,
        ...context,
      }),
  });

  const checkInOpen = isOpen || isCheckInSessionOpen(selectedEvent);

  const { opensAt, closesAt } = getCheckInSchedule(selectedEvent);
  useCheckInBoundaryRefetch({
    opensAt,
    closesAt,
    isOpen: checkInOpen,
    onBoundary: refreshSelectedEvent,
  });

  const hasCheckInActivity =
    canManageCheckIn ||
    checkInOpen ||
    isPastEvent(selectedEvent) ||
    isCheckInEligibleToday(selectedEvent);

  const {
    items: checkedInItemsRaw,
    loaded: checkedInLoaded,
    error: checkedInError,
  } = useCheckIns(selectedEvent?.id, {
    all: true,
    enabled: hasCheckInActivity,
    polling: checkInOpen,
  });
  // Shared hook returns null before first load; web consumers expect an array.
  const checkedInItems = checkedInItemsRaw ?? [];

  const panelError = errorDetail || sessionError || checkedInError;

  useEffect(() => {
    if (panelError) {
      monitorService.error('Failed to load educational event detail', { error: panelError });
    }
  }, [panelError]);

  if (panelError) {
    return (
      <div className={styles.panel}>
        <div className={classNames(styles.card, styles.errorCard)}>
          <span className={styles.errorText}>{lang.DETAIL_LOAD_FAILED}</span>
          <Button type="primary" ghost onClick={refreshSelectedEvent}>
            {lang.RETRY}
          </Button>
        </div>
      </div>
    );
  }

  if (!selectedEvent) {
    return <div className={styles.panel} />;
  }

  if (loadingDetail || !sessionLoaded || (hasCheckInActivity && !checkedInLoaded)) {
    return (
      <div className={styles.panel}>
        <div className={styles.card}>
          <Skeleton active title={{ width: '40%' }} paragraph={{ rows: 6 }} />
        </div>
      </div>
    );
  }

  const event = selectedEvent;
  const isPast = isPastEvent(event);
  const hosts = resolveHosts({ event, users });
  const hostLinks = hosts.length
    ? hosts.map((host, index) => (
        <span key={host.id}>
          {index > 0 && ', '}
          <Link className={styles.hostLink} to={ROUTES.CONTACTS_PREVIEW.replace(':id', host.id)}>
            {host.name}
          </Link>
        </span>
      ))
    : null;
  const speaker = event.attributes?.speaker;
  const webLink = event.webLink ?? event.attributes?.webLink;
  const formatLabel = getFormatLabel(event.attributes?.attendanceMode);
  const checkInWindowLabel = formatCheckInWindowLabel({ event, lang });

  const orgTime = date => {
    const value = formatOrgTime(date);
    return value ? { label: <strong>{lang.ORG_TIME}</strong>, value } : null;
  };

  return (
    <div className={styles.panel}>
      <section className={styles.card}>
        <div className={styles.titleRow}>
          <h2 className={styles.title}>
            {event.title}
            {event.scheduleEventGroupId && (
              <FontAwesomeIcon
                icon={faArrowsRotate}
                className={styles.recurringIcon}
                title={lang.RECURRING_EVENT}
                aria-label={lang.RECURRING_EVENT}
              />
            )}
          </h2>
          {checkInOpen && <span className={styles.statusTag}>{lang.SESSION_OPEN}</span>}
          {isHost && <span className={styles.hostTag}>{lang.HOST}</span>}
          {isManager && (
            <div className={styles.titleActions}>
              <DeleteEventButton event={event} disabled={checkInOpen} />
              {isPast ? (
                <Tooltip title={lang.EDIT_DISABLED_PAST} placement="top">
                  <span className={styles.editButtonWrap}>
                    <Button className={styles.editButton} disabled>
                      {lang.EDIT_EVENT}
                    </Button>
                  </span>
                </Tooltip>
              ) : (
                <Button
                  className={styles.editButton}
                  onClick={() => {
                    analyticsService.track(EVENTS.SCHEDULE.EDIT_EVENT_CLICK, {
                      type: SCHEDULE_EVENT_TYPES.EDUCATIONAL,
                      eventId: event.id,
                      recurring: !!event.scheduleEventGroupId,
                    });
                    openEditForm(event);
                  }}>
                  {lang.EDIT_EVENT}
                </Button>
              )}
            </div>
          )}
        </div>

        <span className={styles.metaLine}>
          <FontAwesomeIcon icon={faCalendar} className={styles.metaIcon} />
          {formatDateWithTime(event.startTime)}
          {event.location && (
            <>
              <span className={styles.metaSep}>·</span>
              <FontAwesomeIcon icon={faLocationDot} className={styles.metaIcon} />
              {event.location}
            </>
          )}
        </span>

        {canManageCheckIn ? (
          <CheckInToggleRow
            event={event}
            session={session}
            isOpen={checkInOpen}
            pending={pending}
            open={open}
            close={close}
            canManageCheckIn={canManageCheckIn}
            checkedInItems={checkedInItems}
            onShowQr={setQrOpen}
          />
        ) : (
          <SelfAttestButton event={event} />
        )}

        <div className={styles.infoGrid}>
          <InfoField label={lang.HOST} value={hostLinks} />
          <InfoField
            label={schedulesLang.DETAILS_DATE}
            value={formatDayWithWeekday(event.startTime)}
          />
          <InfoField label={lang.SPEAKERS} value={speaker} />
          <div className={styles.timeRow}>
            <InfoField
              label={lang.START_TIME}
              value={formatTime(event.startTime)}
              sub={orgTime(event.startTime)}
            />
            <InfoField
              label={lang.END_TIME}
              value={formatTime(event.endTime)}
              sub={orgTime(event.endTime)}
            />
          </div>
          {event.group?.recurrence?.cadence && (
            <InfoField
              label={createLang.REPEAT_LABEL}
              value={createLang[CADENCE_LABEL_KEYS[event.group.recurrence.cadence]]}
            />
          )}
          {formatLabel && <InfoField label={lang.FORMAT} value={formatLabel} />}
          <InfoField
            label={lang.CHECK_IN_METHOD}
            value={
              event.attributes?.allowSelfAttestation
                ? lang.CHECK_IN_METHOD_OPTION_SELF_ATTESTATION
                : lang.CHECK_IN_METHOD_OPTION_QR
            }
          />
          <InfoField label={lang.CHECK_IN_WINDOW} value={checkInWindowLabel} />
          <InfoField label={lang.LOCATION} value={event.location} />
          {webLink && (
            <InfoField
              label={lang.VIRTUAL_LINK}
              value={
                <a
                  className={styles.link}
                  href={toExternalUrl(webLink)}
                  target="_blank"
                  rel="noopener noreferrer">
                  {webLink}
                </a>
              }
            />
          )}
        </div>
      </section>

      <section className={styles.card}>
        <EventResources event={event} canEdit={isManager} disabled={isPast} />
      </section>

      <CheckedInCard
        event={event}
        isOpen={checkInOpen}
        canManageCheckIn={canManageCheckIn}
        items={checkedInItems}
        users={users}
      />

      {canManageCheckIn && (
        <ShowQrModal
          open={qrOpen}
          onClose={() => setQrOpen(false)}
          eventId={event.id}
          eventTitle={event.title}
          isStatic={isStaticQr(event)}
          totpSecret={session?.totpSecret}
          step={session?.step}
        />
      )}
      <CheckInClosedModal
        open={!!closedSummary}
        summary={closedSummary}
        onClose={clearClosedSummary}
      />
    </div>
  );
}
